import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import styled, { useTheme } from 'styled-components';

import { createUserTrace } from '../../api/Optimise';
import errorDisc from '../../assets/svg/disc/error-disc.svg';
import withContentfulTag from '../../hocs/withContentfulTag';
import { usePersistedState } from '../../hooks';
import type { ContentfulTagProps } from '../../types/propTypes';
import SolarIconDisc from '../../ui/Icon/SolarIconDisc';
import OnboardingDeleteAccountModal from '../../ui/Modal/DeleteAccount/OnboardingDeleteAccountModal';

const CtaWrapperDiv = styled.div`
  position: relative;
  border-radius: 4px;
  background: ${(props) => props.theme.colors.gradients.solarReverse};
  margin: 0 -25px 0 -25px;
  padding: 30px;
`;

const InnerDiv = styled.div`
  background: ${(props) => props.theme.colors.white};
  padding: 30px 15px 15px 15px;
  border-radius: 4px;
`;

const StyledReactMarkdown = styled(ReactMarkdown)`
  h2 {
    color: ${(props) => props.theme.colors.black};
  }
`;

const StyledDeleteAccountParagraph = styled.p`
  position: relative;
  top: 4px;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 0;

  button {
    font-size: 14px;
    line-height: 1.5;
  }
`;

const NotCompatibleContainer = ({
  loading,
  fetched,
  contentful,
}: Pick<ContentfulTagProps, 'contentful' | 'loading' | 'fetched'>) => {
  const theme = useTheme();
  useEffect(() => {
    const asyncCreateUserTrace = async () => {
      await createUserTrace('Smart Meter Not Compatible V2');
    };
    asyncCreateUserTrace().catch((e: unknown) => console.error(e));
  }, []);

  const [isDeleting] = useState(false);
  const [, setDeleteError] = usePersistedState('deleteError', undefined);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  return (
    !loading &&
    fetched && (
      <>
        <OnboardingDeleteAccountModal
          show={showDeleteAccountModal}
          onHide={() => setShowDeleteAccountModal(false)}
          setDeleteError={setDeleteError}
          initialReason="Didn't complete onboarding. Smart meter incompatible."
        />
        <p>
          <img src={errorDisc} alt="Warning" />
        </p>
        <h1 className="pb-3">{contentful?.title}</h1>
        <ReactMarkdown className="pb-4">{contentful?.body ?? ''}</ReactMarkdown>
        <SolarIconDisc top={-30} />
        <CtaWrapperDiv>
          <InnerDiv>
            {contentful?.bodyEnd && <StyledReactMarkdown>{contentful.bodyEnd}</StyledReactMarkdown>}
            {contentful?.primaryButtonText && (
              <Button
                className="mb-0 mt-2 w-100"
                onClick={async () => {
                  await createUserTrace('Smart Meter Not Compatible Solar CTA');
                  return (window.location.href = contentful?.link ?? '');
                }}
              >
                {contentful.primaryButtonText}
              </Button>
            )}
          </InnerDiv>
        </CtaWrapperDiv>

        <div className="mt-3">
          <StyledDeleteAccountParagraph>
            No longer want a {theme.language.appName} account?{' '}
            <Button variant="inline-link" onClick={() => setShowDeleteAccountModal(true)} disabled={isDeleting}>
              Delete it here
            </Button>
          </StyledDeleteAccountParagraph>
        </div>
      </>
    )
  );
};

export default withContentfulTag(NotCompatibleContainer, 'web_app_onboarding_not_compatible_page_v2');
